import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save as SaveIcon } from '@mui/icons-material';
import { pipelineApi, type Pipeline, type Step } from '@/api/pipeline';
import { stepApi } from '@/api/step';

interface PipelineEditModalProps {
  open: boolean;
  onClose: () => void;
  pipeline: Pipeline;
}

interface ImportRules {
  dont_import_open_to_work: boolean;
  dont_import_less_that_500_connections: boolean;
  dont_import_no_photo_on_profile: boolean;
  dont_import_free_linkedin_account: boolean;
  dont_import_duplicate_prospects_with_same_owner: boolean;
  delete_other_waiting_prospects_with_same_owner: boolean;
}

interface StepTemplateState {
  message_template: string;
  ai_messaging: boolean;
}

const ACTIVE_DAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
  'Weekdays',
  'Weekends',
];

const defaultImportRules: ImportRules = {
  dont_import_open_to_work: false,
  dont_import_less_that_500_connections: false,
  dont_import_no_photo_on_profile: false,
  dont_import_free_linkedin_account: false,
  dont_import_duplicate_prospects_with_same_owner: true,
  delete_other_waiting_prospects_with_same_owner: false,
};

const normalizeImportRules = (value: Pipeline['import_rules'] | undefined): ImportRules => {
  return {
    dont_import_open_to_work:
      value?.dont_import_open_to_work ?? defaultImportRules.dont_import_open_to_work,
    dont_import_less_that_500_connections:
      value?.dont_import_less_that_500_connections ??
      defaultImportRules.dont_import_less_that_500_connections,
    dont_import_no_photo_on_profile:
      value?.dont_import_no_photo_on_profile ??
      defaultImportRules.dont_import_no_photo_on_profile,
    dont_import_free_linkedin_account:
      value?.dont_import_free_linkedin_account ??
      defaultImportRules.dont_import_free_linkedin_account,
    dont_import_duplicate_prospects_with_same_owner:
      value?.dont_import_duplicate_prospects_with_same_owner ??
      defaultImportRules.dont_import_duplicate_prospects_with_same_owner,
    delete_other_waiting_prospects_with_same_owner:
      value?.delete_other_waiting_prospects_with_same_owner ??
      defaultImportRules.delete_other_waiting_prospects_with_same_owner,
  };
};

export const PipelineEditModal: React.FC<PipelineEditModalProps> = ({
  open,
  onClose,
  pipeline: initialPipeline,
}) => {
  const queryClient = useQueryClient();
  const [currentPipeline, setCurrentPipeline] = useState<Pipeline>(initialPipeline);
  const [name, setName] = useState<string>(initialPipeline.name || '');
  const [activeDays, setActiveDays] = useState<string[]>(initialPipeline.active_days || []);
  const [activeHoursFrom, setActiveHoursFrom] = useState<number | ''>(
    typeof initialPipeline.active_hours_from === 'number' ? initialPipeline.active_hours_from : '',
  );
  const [activeHoursTo, setActiveHoursTo] = useState<number | ''>(
    typeof initialPipeline.active_hours_to === 'number' ? initialPipeline.active_hours_to : '',
  );
  const [importRules, setImportRules] = useState<ImportRules>(
    normalizeImportRules(initialPipeline.import_rules),
  );
  const [stepTemplates, setStepTemplates] = useState<Record<string, StepTemplateState>>({});

  const { data: stepsData } = useQuery({
    queryKey: ['steps', currentPipeline.key],
    queryFn: () => pipelineApi.getSteps({ pipeline_uuid: currentPipeline.key }),
    enabled: open,
  });

  const steps = useMemo<Step[]>(() => stepsData?.output.items || [], [stepsData]);
  const stepsWithTemplates = useMemo(
    () => steps.filter((s) => s.message_template !== null && s.message_template !== undefined),
    [steps],
  );

  const connectionRequestMaxLength = 300;

  const getMessageTemplateError = (step: Step, messageTemplate: string): string | null => {
    // Apply only to connection request steps.
    // Backend Step.Actions.SEND_CONNECTION_REQUEST.value is "Send Connection Request".
    if ((step.action || '') !== 'Send Connection Request') {
      return null;
    }
    if ((messageTemplate || '').length > connectionRequestMaxLength) {
      return `Message is too long for a connection request (${messageTemplate.length}/${connectionRequestMaxLength} characters).`;
    }
    return null;
  };

  const hasInvalidConnectionRequestMessageTemplate = useMemo(() => {
    for (const step of stepsWithTemplates) {
      if ((step.action || '') !== 'Send Connection Request') continue;
      const template = stepTemplates[step.key]?.message_template ?? step.message_template ?? '';
      if ((template || '').length > connectionRequestMaxLength) return true;
    }
    return false;
  }, [connectionRequestMaxLength, stepTemplates, stepsWithTemplates]);

  const hasValidDuplicateProspectRule =
    importRules.dont_import_duplicate_prospects_with_same_owner !==
    importRules.delete_other_waiting_prospects_with_same_owner;

  useEffect(() => {
    if (stepsWithTemplates.length === 0) {
      return;
    }
    setStepTemplates((prev) => {
      const next = { ...prev };
      stepsWithTemplates.forEach((step) => {
        if (!next[step.key]) {
          next[step.key] = {
            message_template: step.message_template || '',
            ai_messaging: step.ai_messaging || false,
          };
        }
      });
      return next;
    });
  }, [stepsWithTemplates]);

  useEffect(() => {
    if (open) {
      // Get the latest pipeline from cache
      let latestPipeline = initialPipeline;
      if (initialPipeline.stream?.key) {
        const cachedData = queryClient.getQueryData<any>(['pipelines', initialPipeline.stream.key]);
        if (cachedData?.pages) {
          for (const page of cachedData.pages) {
            const found = page.output?.items?.find((p: Pipeline) => p.key === initialPipeline.key);
            if (found) {
              latestPipeline = found;
              break;
            }
          }
        }
      }
      setCurrentPipeline(latestPipeline);
      setName(latestPipeline.name || '');
      setActiveDays(latestPipeline.active_days || []);
      setActiveHoursFrom(
        typeof latestPipeline.active_hours_from === 'number' ? latestPipeline.active_hours_from : '',
      );
      setActiveHoursTo(typeof latestPipeline.active_hours_to === 'number' ? latestPipeline.active_hours_to : '');
      setImportRules(normalizeImportRules(latestPipeline.import_rules));
      setStepTemplates({});
    }
  }, [open, initialPipeline.key, initialPipeline.stream?.key, queryClient]);

  const updatePipelineMutation = useMutation({
    mutationFn: (data: Parameters<typeof pipelineApi.updatePipeline>[1]) =>
      pipelineApi.updatePipeline(currentPipeline.key, data),
    onSuccess: (response) => {
      const updatedPipeline = response.output;
      
      // Update the infinite query cache with the updated pipeline
      if (currentPipeline.stream?.key) {
        queryClient.setQueriesData(
          { queryKey: ['pipelines', currentPipeline.stream.key] },
          (oldData: any) => {
            if (!oldData) return oldData;
            
            return {
              ...oldData,
              pages: oldData.pages.map((page: any) => ({
                ...page,
                output: {
                  ...page.output,
                  items: page.output.items.map((item: Pipeline) =>
                    item.key === updatedPipeline.key ? updatedPipeline : item
                  ),
                },
              })),
            };
          }
        );
      }
      
      // Update current pipeline state
      setCurrentPipeline(updatedPipeline);
      
      // Refetch to ensure consistency
      queryClient.refetchQueries({ queryKey: ['pipelines', currentPipeline.stream?.key] });
      queryClient.invalidateQueries({ queryKey: ['pipelines'] });
    },
  });

  const updateStepsMutation = useMutation({
    mutationFn: async (templates: Record<string, StepTemplateState>) => {
      const updates = Object.entries(templates).map(([stepUuid, templateData]) =>
        stepApi.updateStep(stepUuid, {
          message_template: templateData.message_template || null,
          ai_messaging: templateData.ai_messaging,
        }),
      );
      await Promise.all(updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['steps', currentPipeline.key] });
    },
  });

  const isSaving = updatePipelineMutation.isPending || updateStepsMutation.isPending;

  const hasChanges =
    (name.trim() || '') !== (currentPipeline.name || '') ||
    JSON.stringify(activeDays || []) !== JSON.stringify(currentPipeline.active_days || []) ||
    (activeHoursFrom || '') !== (currentPipeline.active_hours_from ?? '') ||
    (activeHoursTo || '') !== (currentPipeline.active_hours_to ?? '') ||
    JSON.stringify(importRules) !== JSON.stringify(normalizeImportRules(currentPipeline.import_rules)) ||
    Object.keys(stepTemplates).some((key) => {
      const original = stepsWithTemplates.find((s) => s.key === key);
      if (!original) return false;
      const current = stepTemplates[key];
      return (
        (original.message_template || '') !== (current.message_template || '') ||
        !!original.ai_messaging !== !!current.ai_messaging
      );
    });

  const handleSave = async () => {
    if (!hasChanges) return;
    if (hasInvalidConnectionRequestMessageTemplate) return;
    if (!hasValidDuplicateProspectRule) return;

    try {
      await updatePipelineMutation.mutateAsync({
        name: name.trim() || undefined,
        active_days: activeDays,
        active_hours_from: activeHoursFrom === '' ? undefined : activeHoursFrom,
        active_hours_to: activeHoursTo === '' ? undefined : activeHoursTo,
        import_rules: importRules,
      });
      if (Object.keys(stepTemplates).length > 0) {
        await updateStepsMutation.mutateAsync(stepTemplates);
      }
    } catch {
      // Errors are surfaced via mutation error states
    }
  };

  const handleReset = () => {
    setName(currentPipeline.name || '');
    setActiveDays(currentPipeline.active_days || []);
    setActiveHoursFrom(
      typeof currentPipeline.active_hours_from === 'number' ? currentPipeline.active_hours_from : '',
    );
    setActiveHoursTo(typeof currentPipeline.active_hours_to === 'number' ? currentPipeline.active_hours_to : '');
    setImportRules(normalizeImportRules(currentPipeline.import_rules));
    setStepTemplates({});
  };

  const handleClose = () => {
    // Clear previous mutation errors when closing
    updatePipelineMutation.reset();
    updateStepsMutation.reset();
    onClose();
  };

  const handleSaveAndClose = async () => {
    await handleSave();
    if (!updatePipelineMutation.isError && !updateStepsMutation.isError) {
      handleClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Pipeline Settings</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label="Pipeline Name"
            variant="outlined"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={isSaving}
            required
            inputProps={{ maxLength: 1024 }}
            helperText={`${name.length}/1024 characters`}
          />

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 160 }}>
              <Typography variant="subtitle2" sx={{ mr: 1 }}>
                Status:
              </Typography>
              <Chip size="small" label={currentPipeline.status} />
            </Box>

            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Active Days</InputLabel>
              <Select
                multiple
                label="Active Days"
                value={activeDays}
                onChange={(e) => {
                  const value =
                    typeof e.target.value === 'string'
                      ? e.target.value.split(',')
                      : (e.target.value as string[]);
                  setActiveDays(value);
                }}
                disabled={isSaving}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {ACTIVE_DAYS.map((day) => (
                  <MenuItem key={day} value={day}>
                    <Checkbox checked={activeDays.indexOf(day) > -1} />
                    <Typography>{day}</Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Active From</InputLabel>
              <Select
                label="Active From"
                value={activeHoursFrom === '' ? '' : String(activeHoursFrom)}
                onChange={(e) =>
                  setActiveHoursFrom(e.target.value === '' ? '' : Number(e.target.value))
                }
                disabled={isSaving}
              >
                <MenuItem value="">
                  <em>Any</em>
                </MenuItem>
                {Array.from({ length: 25 }, (_, i) => (
                  <MenuItem key={i} value={String(i)}>
                    {i}:00
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Active To</InputLabel>
              <Select
                label="Active To"
                value={activeHoursTo === '' ? '' : String(activeHoursTo)}
                onChange={(e) =>
                  setActiveHoursTo(e.target.value === '' ? '' : Number(e.target.value))
                }
                disabled={isSaving}
              >
                <MenuItem value="">
                  <em>Any</em>
                </MenuItem>
                {Array.from({ length: 25 }, (_, i) => (
                  <MenuItem key={i} value={String(i)}>
                    {i}:00
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Import Rules
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={importRules.dont_import_open_to_work}
                    onChange={(e) =>
                      setImportRules((prev) => ({
                        ...prev,
                        dont_import_open_to_work: e.target.checked,
                      }))
                    }
                    disabled={isSaving}
                  />
                }
                label="Don't import prospects open to work"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={importRules.dont_import_less_that_500_connections}
                    onChange={(e) =>
                      setImportRules((prev) => ({
                        ...prev,
                        dont_import_less_that_500_connections: e.target.checked,
                      }))
                    }
                    disabled={isSaving}
                  />
                }
                label="Don't import prospects with less than 500 connections"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={importRules.dont_import_no_photo_on_profile}
                    onChange={(e) =>
                      setImportRules((prev) => ({
                        ...prev,
                        dont_import_no_photo_on_profile: e.target.checked,
                      }))
                    }
                    disabled={isSaving}
                  />
                }
                label="Don't import prospects without profile photo"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={importRules.dont_import_free_linkedin_account}
                    onChange={(e) =>
                      setImportRules((prev) => ({
                        ...prev,
                        dont_import_free_linkedin_account: e.target.checked,
                      }))
                    }
                    disabled={isSaving}
                  />
                }
                label="Don't import free LinkedIn accounts"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 0.5 }}>
                Duplicate prospect (same operator): choose one
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={importRules.dont_import_duplicate_prospects_with_same_owner}
                    onChange={(e) =>
                      setImportRules((prev) => ({
                        ...prev,
                        dont_import_duplicate_prospects_with_same_owner: e.target.checked,
                        delete_other_waiting_prospects_with_same_owner: !e.target.checked,
                      }))
                    }
                    disabled={isSaving}
                  />
                }
                label="Don't import duplicate prospects with same owner"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={importRules.delete_other_waiting_prospects_with_same_owner}
                    onChange={(e) =>
                      setImportRules((prev) => ({
                        ...prev,
                        delete_other_waiting_prospects_with_same_owner: e.target.checked,
                        dont_import_duplicate_prospects_with_same_owner: !e.target.checked,
                      }))
                    }
                    disabled={isSaving}
                  />
                }
                label="Delete other waiting prospects with same owner"
              />
              {!hasValidDuplicateProspectRule && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  Duplicate prospect handling: select exactly one option.
                </Alert>
              )}
            </Box>
          </Box>

          {stepsWithTemplates.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Message Templates
              </Typography>
              {stepsWithTemplates.map((step) => {
                const current = stepTemplates[step.key] || {
                  message_template: step.message_template || '',
                  ai_messaging: step.ai_messaging || false,
                };
                const messageError = getMessageTemplateError(step, current.message_template);
                return (
                  <Box
                    key={step.key}
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 1.5,
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      {step.name}
                    </Typography>
                    <Box
                      component="textarea"
                      value={current.message_template}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setStepTemplates((prev) => ({
                          ...prev,
                          [step.key]: {
                            ...current,
                            message_template: e.target.value,
                          },
                        }))
                      }
                      disabled={isSaving}
                      style={{
                        width: '100%',
                        minHeight: 96,
                        padding: 8,
                        fontFamily: 'inherit',
                        fontSize: '0.875rem',
                        borderRadius: 4,
                        border: messageError
                          ? '1px solid #d32f2f'
                          : '1px solid rgba(0,0,0,0.23)',
                        boxSizing: 'border-box',
                        resize: 'vertical',
                      }}
                    />
                    {messageError && (
                      <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                        {messageError}
                      </Typography>
                    )}
                    <FormControlLabel
                      sx={{ mt: 0.5 }}
                      control={
                        <Checkbox
                          checked={current.ai_messaging}
                          onChange={(e) =>
                            setStepTemplates((prev) => ({
                              ...prev,
                              [step.key]: {
                                ...current,
                                ai_messaging: e.target.checked,
                              },
                            }))
                          }
                          disabled={isSaving}
                        />
                      }
                      label="Use AI Messaging"
                    />
                  </Box>
                );
              })}
            </Box>
          )}

          {(updatePipelineMutation.isError || updateStepsMutation.isError) && (
            <Alert severity="error">
              Failed to save pipeline changes. Please review your inputs and try again.
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          startIcon={
            isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />
          }
          onClick={handleSaveAndClose}
          disabled={
            !hasChanges ||
            isSaving ||
            hasInvalidConnectionRequestMessageTemplate ||
            !hasValidDuplicateProspectRule
          }
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
        <Button onClick={handleReset} disabled={!hasChanges || isSaving}>
          Reset
        </Button>
        <Button onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};
